import { useCallback, useState } from "react";

import formatTime from "../../../utils/formatTime";
import { useAppContext } from "../../../hooks/useAppContext";
import TimeInput from "./TimeInput";
import useGetVideoInfo from "../../../hooks/useGetVideoInfo";

import type { TTimeInputProps } from "./TimeInput";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

const VideoEditorEndTimeInput: React.FC<TTimeInputProps> = (props) => {
  const {
    timelineStartTime,
    setTimelineEndTime,
    timelineEndTime,
    isProcessing,
  } = useAppContext();
  const { duration } = useGetVideoInfo();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  const [error, setError] = useState("");

  const handleBlur = useCallback<NonNullable<TTimeInputProps["onBlur"]>>(
    (_, endPosition) => {
      // Validate that the end time is within bounds and after the start time
      if (endPosition <= duration && endPosition > timelineStartTime) {
        setError("");
        
        // TELEMETRY: User successfully inputted a manual timestamp (High precision intent)
        if (reportBehavior) {
          reportBehavior("manual_timestamp_entry", 2, "interest");
        }
        
        return setTimelineEndTime(endPosition);
      }

      // TELEMETRY: User entered an impossible timestamp (Friction / Logic Error)
      if (reportBehavior) {
        reportBehavior("invalid_time_input", 3, "behavior");
      }
      
      setError("Invalid time");
    },
    [duration, setTimelineEndTime, timelineStartTime, reportBehavior]
  );

  const value = formatTime(timelineEndTime);

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  return (
    <TimeInput
      disabled={isProcessing}
      error={!!error}
      label={error || "End time"}
      value={value}
      onBlur={handleBlur}
      sx={{
        // ADAPTIVE UI: Pulse red if there's an error and the user is experiencing high cognitive load
        animation: error && highlightCTA && !isProcessing ? "pulse-time-error 2s infinite" : "none",
        
        "& .MuiInputBase-root": {
          color: isDarkMode ? "#ffffff" : "inherit",
          backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "inherit",
          transition: "background-color 0.3s ease, color 0.3s ease",
        },
        "& .MuiInputLabel-root": {
          color: error ? (isDarkMode ? "#ff5252" : "#d32f2f") : (isDarkMode ? "rgba(255,255,255,0.7)" : "inherit"),
        },

        "@keyframes pulse-time-error": {
          "0%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.4)" },
          "70%": { boxShadow: "0 0 0 6px rgba(211, 47, 47, 0)" },
          "100%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0)" }
        },
        ...(props.sx || {}) // Safely spread any incoming sx props from parents
      }}
      {...props}
    />
  );
};

export default VideoEditorEndTimeInput;